/**
 * MIPTech Simple Widget - Functional Chatbot Widget
 * A simplified but functional version using existing components
 */

import { WIDGET_STATES, THEMES, POSITIONS, EVENT_TYPES } from './config/constants.js';

/**
 * Simple MIPTech Widget Class
 */
export default class SimpleMIPTechWidget {
  constructor(config = {}) {
    this.version = '1.0.0-simple';
    this.instanceId = this.generateInstanceId();

    // Basic configuration
    this.config = {
      tenantId: config.tenantId || 'default',
      apiKey: config.apiKey || '',
      theme: config.theme || THEMES.DEFAULT,
      position: config.position || POSITIONS.BOTTOM_RIGHT,
      autoInit: config.autoInit !== false,
      brandColor: config.brandColor || '#007cba',
      accentColor: config.accentColor || '#0073aa',
      ...config
    };

    // State management
    this.state = WIDGET_STATES.INITIALIZING;
    this.isVisible = false;
    this.initialized = false;

    // DOM elements
    this.container = null;
    this.toggleButton = null;
    this.chatWindow = null;

    // Auto-initialize
    if (this.config.autoInit) {
      this.init();
    }
  }

  /**
   * Initialize the widget
   */
  async init() {
    if (this.initialized) return;

    try {
      console.log('MIPTech Widget: Initializing...');

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          document.addEventListener('DOMContentLoaded', resolve);
        });
      }

      // Create widget elements
      this.createContainer();
      this.createToggleButton();
      this.createChatWindow();
      this.setupEventListeners();

      // Set initial state
      this.initialized = true;
      this.state = WIDGET_STATES.READY;

      console.log('MIPTech Widget: Ready');

      // Emit ready event
      this.emit(EVENT_TYPES.WIDGET_READY);

    } catch (error) {
      console.error('MIPTech Widget: Initialization failed', error);
      this.state = WIDGET_STATES.ERROR;
    }
  }

  /**
   * Create main container
   */
  createContainer() {
    this.container = document.createElement('div');
    this.container.id = `miptech-widget-${this.instanceId}`;
    this.container.className = `miptech-widget-container miptech-widget-${this.config.position}`;
    this.container.setAttribute('data-theme', this.config.theme);

    // Inject styles
    this.injectStyles();

    document.body.appendChild(this.container);
  }

  /**
   * Create toggle button
   */
  createToggleButton() {
    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'miptech-toggle-button';
    this.toggleButton.innerHTML = this.getChatIcon();
    this.toggleButton.title = 'Chat con noi!';
    this.toggleButton.setAttribute('aria-label', 'Apri chat');
    this.toggleButton.setAttribute('aria-expanded', 'false');

    this.toggleButton.addEventListener('click', () => this.toggle());

    this.container.appendChild(this.toggleButton);
  }

  /**
   * Create chat window
   */
  createChatWindow() {
    this.chatWindow = document.createElement('div');
    this.chatWindow.className = 'miptech-chat-window';
    this.chatWindow.style.display = 'none';

    this.chatWindow.innerHTML = `
      <div class="miptech-chat-header">
        <div class="miptech-chat-title">
          <h3>Chat di Supporto</h3>
          <span class="miptech-status online">Online</span>
        </div>
        <button class="miptech-close-btn" aria-label="Chiudi chat">×</button>
      </div>
      <div class="miptech-chat-messages" id="miptech-messages-${this.instanceId}">
        <div class="miptech-message bot">
          <div class="miptech-message-avatar">🤖</div>
          <div class="miptech-message-content">
            <p>Ciao! Come posso aiutarti oggi?</p>
            <span class="miptech-message-time">${new Date().toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</span>
          </div>
        </div>
      </div>
      <div class="miptech-chat-input">
        <input type="text" placeholder="Scrivi un messaggio..." id="miptech-input-${this.instanceId}">
        <button class="miptech-send-btn" aria-label="Invia messaggio">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    `;

    // Setup chat interactions
    this.setupChatInteractions();

    this.container.appendChild(this.chatWindow);
  }

  /**
   * Setup chat interactions
   */
  setupChatInteractions() {
    const closeBtn = this.chatWindow.querySelector('.miptech-close-btn');
    const input = this.chatWindow.querySelector(`#miptech-input-${this.instanceId}`);
    const sendBtn = this.chatWindow.querySelector('.miptech-send-btn');

    closeBtn.addEventListener('click', () => this.hide());

    const sendMessage = () => {
      const message = input.value.trim();
      if (message) {
        this.addMessage('user', message);
        input.value = '';

        // Simulate bot response
        setTimeout(() => {
          const responses = [
            'Grazie per il tuo messaggio! Ti risponderà presto un operatore.',
            'Ho capito la tua richiesta. Stiamo cercando la soluzione migliore per te.',
            'Interessante! Fammi controllare le informazioni per te.',
            'Perfetto! Ti aiuto subito con questo.'
          ];
          const randomResponse = responses[Math.floor(Math.random() * responses.length)];
          this.addMessage('bot', randomResponse);
        }, 1000 + Math.random() * 2000);
      }
    };

    sendBtn.addEventListener('click', sendMessage);
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        sendMessage();
      }
    });
  }

  /**
   * Add message to chat
   */
  addMessage(type, content) {
    const messagesContainer = this.chatWindow.querySelector(`#miptech-messages-${this.instanceId}`);
    const messageDiv = document.createElement('div');
    messageDiv.className = `miptech-message ${type}`;

    const time = new Date().toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'});
    const avatar = type === 'bot' ? '🤖' : '👤';

    messageDiv.innerHTML = `
      <div class="miptech-message-avatar">${avatar}</div>
      <div class="miptech-message-content">
        <p>${content}</p>
        <span class="miptech-message-time">${time}</span>
      </div>
    `;

    messagesContainer.appendChild(messageDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    });
  }

  /**
   * Show widget
   */
  show() {
    if (this.isVisible) return;

    this.isVisible = true;
    this.chatWindow.style.display = 'block';
    this.toggleButton.innerHTML = this.getCloseIcon();
    this.toggleButton.setAttribute('aria-expanded', 'true');

    // Animate in
    requestAnimationFrame(() => {
      this.chatWindow.style.transform = 'translateY(0)';
      this.chatWindow.style.opacity = '1';
    });

    // Focus input
    const input = this.chatWindow.querySelector(`#miptech-input-${this.instanceId}`);
    if (input) {
      setTimeout(() => input.focus(), 300);
    }

    this.emit(EVENT_TYPES.WIDGET_OPENED);
    console.log('MIPTech Widget: Opened');
  }

  /**
   * Hide widget
   */
  hide() {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.chatWindow.style.transform = 'translateY(20px)';
    this.chatWindow.style.opacity = '0';
    this.toggleButton.innerHTML = this.getChatIcon();
    this.toggleButton.setAttribute('aria-expanded', 'false');

    setTimeout(() => {
      this.chatWindow.style.display = 'none';
    }, 300);

    this.emit(EVENT_TYPES.WIDGET_CLOSED);
    console.log('MIPTech Widget: Closed');
  }

  /**
   * Toggle widget visibility
   */
  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Emit event
   */
  emit(eventType, data = {}) {
    const event = new CustomEvent(eventType, {
      detail: { ...data, widget: this }
    });
    document.dispatchEvent(event);
  }

  /**
   * Get chat icon
   */
  getChatIcon() {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
      </svg>
    `;
  }

  /**
   * Get close icon
   */
  getCloseIcon() {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    `;
  }

  /**
   * Inject CSS styles
   */
  injectStyles() {
    if (document.getElementById('miptech-widget-styles')) return;

    const style = document.createElement('style');
    style.id = 'miptech-widget-styles';
    style.textContent = `
      .miptech-widget-container {
        position: fixed;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.4;
      }

      .miptech-widget-bottom-right {
        bottom: 20px;
        right: 20px;
      }

      .miptech-widget-bottom-left {
        bottom: 20px;
        left: 20px;
      }

      .miptech-toggle-button {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        background: ${this.config.brandColor};
        color: white;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .miptech-toggle-button:hover {
        transform: scale(1.05);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
      }

      .miptech-chat-window {
        position: absolute;
        bottom: 70px;
        right: 0;
        width: 350px;
        height: 500px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.3s ease;
        overflow: hidden;
      }

      .miptech-chat-header {
        background: ${this.config.brandColor};
        color: white;
        padding: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .miptech-chat-title h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
      }

      .miptech-status {
        font-size: 12px;
        opacity: 0.9;
      }

      .miptech-status.online:before {
        content: "●";
        color: #4CAF50;
        margin-right: 4px;
      }

      .miptech-close-btn {
        background: none;
        border: none;
        color: white;
        font-size: 20px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: background 0.2s;
      }

      .miptech-close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      .miptech-chat-messages {
        flex: 1;
        padding: 16px;
        overflow-y: auto;
        background: #f8f9fa;
      }

      .miptech-message {
        display: flex;
        margin-bottom: 16px;
        max-width: 85%;
      }

      .miptech-message.user {
        margin-left: auto;
        flex-direction: row-reverse;
      }

      .miptech-message-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        margin: 0 8px;
        flex-shrink: 0;
      }

      .miptech-message.bot .miptech-message-avatar {
        background: ${this.config.accentColor};
      }

      .miptech-message.user .miptech-message-avatar {
        background: #6c757d;
      }

      .miptech-message-content {
        background: white;
        padding: 12px 16px;
        border-radius: 18px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      .miptech-message.user .miptech-message-content {
        background: ${this.config.brandColor};
        color: white;
      }

      .miptech-message-content p {
        margin: 0;
      }

      .miptech-message-time {
        font-size: 11px;
        opacity: 0.6;
        display: block;
        margin-top: 4px;
      }

      .miptech-chat-input {
        padding: 16px;
        background: white;
        border-top: 1px solid #e9ecef;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .miptech-chat-input input {
        flex: 1;
        border: 1px solid #e9ecef;
        border-radius: 20px;
        padding: 10px 16px;
        outline: none;
        transition: border-color 0.2s;
      }

      .miptech-chat-input input:focus {
        border-color: ${this.config.brandColor};
      }

      .miptech-send-btn {
        background: ${this.config.brandColor};
        color: white;
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
      }

      .miptech-send-btn:hover {
        background: ${this.config.accentColor};
        transform: scale(1.05);
      }

      @media (max-width: 768px) {
        .miptech-chat-window {
          width: 90vw;
          height: 80vh;
          bottom: 70px;
          right: 5vw;
        }

        .miptech-widget-bottom-right {
          bottom: 15px;
          right: 15px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Generate instance ID
   */
  generateInstanceId() {
    return 'miptech_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Get widget version
   */
  getVersion() {
    return this.version;
  }

  /**
   * Check if widget is ready
   */
  isReady() {
    return this.initialized && this.state === WIDGET_STATES.READY;
  }

  /**
   * Destroy widget
   */
  destroy() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    const styles = document.getElementById('miptech-widget-styles');
    if (styles) {
      styles.remove();
    }

    this.initialized = false;
    this.state = WIDGET_STATES.DESTROYED;
    console.log('MIPTech Widget: Destroyed');
  }
}

// Auto-initialize if config provided in window
if (typeof window !== 'undefined' && window.MIPTechWidgetConfig) {
  document.addEventListener('DOMContentLoaded', () => {
    new SimpleMIPTechWidget(window.MIPTechWidgetConfig);
  });
}

// Export for use
window.SimpleMIPTechWidget = SimpleMIPTechWidget;